import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  ArrowUpRight,
  Layers,
} from "lucide-react";
import { useDashboardStats } from "@/lib/api/useDashboardStats";
import { useOpenBuilding } from "@/lib/open-building";
import { cardBase, moduleCard } from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { cn } from "@/lib/utils";
import { useInventoryFilter } from "@/lib/inventory-filter";
import type { Building, BuildingGroup } from "@/lib/api/building";

const MAX_INDIVIDUAL = 8;
const MAX_PER_GROUP = 4;

const STATUS_ORDER: Record<Building["status"], number> = {
  alert: 0,
  warn: 1,
  ok: 2,
};

const STATUS_BADGE: Record<Building["status"], string> = {
  ok: "bg-success-subtle text-success-subtle-foreground border-success/20",
  warn: "bg-warning-subtle text-warning-subtle-foreground border-warning/20",
  alert: "bg-red-500/10 text-red-400 border-red-500/20",
};

const STATUS_LABEL: Record<Building["status"], string> = {
  ok: "Optimoitu",
  warn: "Potentiaalia",
  alert: "Toimi nyt",
};

const STATUS_ICON: Record<
  Building["status"],
  React.ComponentType<{ className?: string }>
> = {
  ok: CheckCircle,
  warn: TrendingUp,
  alert: AlertTriangle,
};

function BuildingCard({ building }: { building: Building }) {
  const Icon = STATUS_ICON[building.status];
  const openBuilding = useOpenBuilding();
  return (
    <button
      type="button"
      onClick={() => openBuilding(building.id)}
      className={`${moduleCard} p-4 text-left flex flex-col gap-3 w-full`}
    >
      <CardGlowOverlay />

      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug truncate">
            {building.name}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {building.city} · {building.building_type}
          </p>
        </div>
        <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-0.5" />
      </div>

      <div className="relative flex items-center justify-between gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[building.status]}`}
        >
          <Icon className="h-2.5 w-2.5" />
          {STATUS_LABEL[building.status]}
        </span>
        {building.optimization_potential_eur_month !== null &&
          building.optimization_potential_eur_month > 0 && (
            <span className="text-[11px] font-medium text-primary">
              €
              {Math.round(
                building.optimization_potential_eur_month,
              ).toLocaleString("fi-FI")}
              /kk
            </span>
          )}
      </div>

      {building.active_optimizations > 0 && (
        <div className="relative text-[10px] text-muted-foreground">
          {building.active_optimizations} aktiivista optimointia
        </div>
      )}
    </button>
  );
}

function BuildingGrid({ buildings }: { buildings: Building[] }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {buildings.map((b) => (
        <BuildingCard key={b.id} building={b} />
      ))}
    </div>
  );
}

/** Initial cap + Näytä lisää / Näytä vähemmän — replaces dead “+N piilotettu” copy. */
function ExpandableBuildingGrid({
  buildings,
  initialLimit,
}: {
  buildings: Building[];
  initialLimit: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const hidden = Math.max(0, buildings.length - initialLimit);
  const visible = expanded ? buildings : buildings.slice(0, initialLimit);

  return (
    <div className="space-y-2">
      <BuildingGrid buildings={visible} />
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[11px] font-medium text-primary hover:underline"
        >
          {expanded
            ? "Näytä vähemmän"
            : `Näytä lisää (${hidden} kiinteistöä)`}
        </button>
      )}
    </div>
  );
}

function GroupSection({
  group,
  buildings,
}: {
  group: BuildingGroup;
  buildings: Building[];
}) {
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em]">
          <Layers className="h-3.5 w-3.5 shrink-0" />
          {group.name}
          <span className="font-normal normal-case tracking-normal text-[11px]">
            · {buildings.length} kiinteistöä
          </span>
        </h3>
      </div>
      <ExpandableBuildingGrid
        buildings={buildings}
        initialLimit={MAX_PER_GROUP}
      />
    </section>
  );
}

export function BuildingList() {
  const state = useDashboardStats();
  const { filter, groupId, activeGroup, groups } = useInventoryFilter();

  if (state.status === "loading") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={cn(cardBase, "h-[104px] animate-pulse")} />
          ))}
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <p className="text-sm text-destructive py-4">
        Kiinteistöjä ei voitu ladata: {state.message}
      </p>
    );
  }

  const applyAttrFilter = (b: Building) => {
    if (filter.city !== "all" && b.city !== filter.city) return false;
    if (filter.type !== "all" && b.building_type !== filter.type) return false;
    if (filter.status !== "all" && b.status !== filter.status) return false;
    return true;
  };

  const sortByStatus = (a: Building, b: Building) =>
    STATUS_ORDER[a.status] - STATUS_ORDER[b.status];

  // ── Individual mode: one group selected ─────────────────────────────────────
  if (groupId !== null && activeGroup !== null) {
    const buildings = state.buildings
      .filter((b) => activeGroup.building_ids.includes(b.id))
      .filter(applyAttrFilter)
      .sort(sortByStatus);

    if (buildings.length === 0) {
      return (
        <p className="text-sm text-muted-foreground py-4">
          Ei kiinteistöjä valituilla rajauksilla.
        </p>
      );
    }

    return (
      <div className="space-y-3">
        <ExpandableBuildingGrid
          buildings={buildings}
          initialLimit={MAX_INDIVIDUAL}
        />
      </div>
    );
  }

  // ── Group mode: no group selected, groups exist ──────────────────────────────
  if (groups.length > 0) {
    const allFiltered = state.buildings.filter(applyAttrFilter);
    const groupedIds = new Set(groups.flatMap((g) => g.building_ids));

    const sections = groups
      .map((group) => ({
        group,
        buildings: allFiltered
          .filter((b) => group.building_ids.includes(b.id))
          .sort(sortByStatus),
      }))
      .filter((s) => s.buildings.length > 0);

    const ungrouped = allFiltered
      .filter((b) => !groupedIds.has(b.id))
      .sort(sortByStatus);

    if (sections.length === 0 && ungrouped.length === 0) {
      return (
        <p className="text-sm text-muted-foreground py-4">
          Ei kiinteistöjä valituilla rajauksilla.
        </p>
      );
    }

    return (
      <div className="space-y-6">
        {sections.map(({ group, buildings }) => (
          <GroupSection key={group.id} group={group} buildings={buildings} />
        ))}

        {ungrouped.length > 0 && (
          <section>
            <h3 className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">
              Ei salkussa
              <span className="font-normal normal-case tracking-normal text-[11px]">
                · {ungrouped.length} kiinteistöä
              </span>
            </h3>
            <ExpandableBuildingGrid
              buildings={ungrouped}
              initialLimit={MAX_PER_GROUP}
            />
          </section>
        )}
      </div>
    );
  }

  // ── Flat mode: no groups defined ─────────────────────────────────────────────
  const sorted = state.buildings.filter(applyAttrFilter).sort(sortByStatus);

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Ei kiinteistöjä valituilla rajauksilla.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <ExpandableBuildingGrid buildings={sorted} initialLimit={MAX_INDIVIDUAL} />
    </div>
  );
}
